'use client';
import { Button } from '@startuptools/ui/joy/Button';
import * as React from 'react';
import { DocumentPreview } from '@startuptools/documents/Editor/DocumentPreview';
import { register } from '@startuptools/documents/Editor/blots';
import {
  AgreementDocument,
  useAgreementQuery,
  useAgreementSuspenseQuery,
  useGenerateAgreementPdfMutation,
  useUpsertValueMutation,
} from './templates.graphql';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { type Field, FieldType } from '@startuptools/documents/Editor/types';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import {
  GqlCreateIdentityInput,
  GqlDocGenState,
  GqlDocumentSignatureMethod,
  GqlSeverity,
  GqlValidatorStatus,
  type GqlAgreementField,
  GqlAgreementFlowStatus,
} from '../../../graphql/base-types.graphql';
import * as Sentry from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { LabeledText } from '@startuptools/ui/LabeledText';
import { DateTime } from 'luxon';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Card } from '@startuptools/ui/joy/Card';
import { Stack } from '@startuptools/ui/joy/Stack';
import { ValidatorError, ValidatorErrorDialog } from '@startuptools/business-ui/ValidatorErrorDialog';
import { orderBy } from 'lodash-es';
import { DocumentSignatures } from '../../../components/document-signatures/DocumentSignatures';
import { DocumentSignaturesDocument } from '../../../components/document-signatures/DocumentSignatures.graphql';
import { Tooltip } from '@startuptools/ui/joy/Tooltip';
import { useFileSignStopMutation } from '../../../graphql/react-operations';

const convertGqlFieldValue = (field: GqlAgreementField): Field => {
  const type = field.type as unknown as FieldType;

  return {
    type,
    value: {
      value: type === FieldType.DATE && field.value?.value ? new Date(field.value.value) : field.value?.value,
      content: type === FieldType.DATE && field.value?.content ? new Date(field.value.content) : field.value?.content,
    },
    name: field.name,
    id: field.id,
    longForm: field.longForm ?? false,
    valueOptions: field.valueOptions ?? [],
  };
};

register();

export function Agreement({ agreementId }: { agreementId: string }) {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [validatorDialog, setValidatorDialog] = React.useState<GqlValidatorStatus[] | undefined>(undefined);
  const { data } = useAgreementSuspenseQuery({ variables: { id: agreementId, companyId } });
  const { startPolling, stopPolling } = useAgreementQuery({ variables: { id: agreementId, companyId } });
  const [generateAgreementPdf, { loading }] = useGenerateAgreementPdfMutation({ refetchQueries: [AgreementDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [upsertValue] = useUpsertValueMutation({ refetchQueries: [AgreementDocument, DocumentSignaturesDocument] });

  React.useEffect(() => {
    if ([GqlDocGenState.Completed, GqlDocGenState.Failed].includes(data.agreement.file.genState)) {
      stopPolling();
    } else {
      startPolling(2000);
    }
  }, [data.agreement.file, startPolling, stopPolling]);

  const generating =
    loading ||
    Boolean(
      data.agreement.file &&
        data.agreement.file.genState !== GqlDocGenState.Completed &&
        data.agreement.file.genState !== GqlDocGenState.Failed &&
        data.agreement.file.genState !== GqlDocGenState.NotApplicable,
    );

  const areAllVariablesSet = React.useMemo(() => {
    return data.agreement.fields.every(field => field.value !== null);
  }, [data.agreement.fields]);

  // eslint-disable-next-line sut/loading-mutation
  const [stopProcess] = useFileSignStopMutation({
    refetchQueries: [AgreementDocument],
  });

  return (
    <Stack gap={2}>
      {validatorDialog && (
        <ValidatorErrorDialog
          allowGenerationWithWarnings={false}
          onClose={() => setValidatorDialog(undefined)}
          preview={() => {
            throw new Error('Unexpected call');
          }}
          hasFatalErrors={validatorDialog.filter(e => e.hasError).some(e => e.severity === GqlSeverity.Error)}
        >
          {orderBy(
            validatorDialog.filter(e => e.hasError),
            e => e.severity,
          ).map(e => (
            <ValidatorError key={e.error?.en.short} error={e} />
          ))}
        </ValidatorErrorDialog>
      )}
      <Card>
        <Typography level="h3">{data.agreement.name}</Typography>
        <LabeledText label={<Trans id="Created At" />}>
          <DateFormat locale={i18n.locale} format={DateTime.DATETIME_SHORT_WITH_SECONDS}>
            {data.agreement.createdAt}
          </DateFormat>
        </LabeledText>

        <LabeledText label={<Trans id="Document Status" />}>
          {data.agreement.status === GqlAgreementFlowStatus.Pending && <Trans id="Draft" />}
          {data.agreement.status === GqlAgreementFlowStatus.Created && <Trans id="PDF ready for signing" />}
          {data.agreement.status === GqlAgreementFlowStatus.Signing && <Trans id="Signing in progress" />}
          {data.agreement.status === GqlAgreementFlowStatus.Completed && <Trans id="Completed" />}
        </LabeledText>

        <DocumentSignatures
          documentId={data.agreement.file.id}
          scriveStatus={data.agreement.file.scriveStatus}
          zignedAgreementStatus={data.agreement.file.zignedAgreementStatus}
          signAllowedAction={data.agreement.file.signAllowedAction}
          signingBlockedReason={
            data.agreement.file.genState === GqlDocGenState.NotApplicable ? (
              <Trans id="Generate PDF first" />
            ) : undefined
          }
          allowManualSignatories={false}
          showReasonForDisabled={false}
          actions={
            [GqlAgreementFlowStatus.Pending, GqlAgreementFlowStatus.Created].includes(data.agreement.status) && (
              <Tooltip
                title={
                  !areAllVariablesSet
                    ? 'The document is missing required fields. Be sure to fill in all inputs below.'
                    : undefined
                }
              >
                <span>
                  <Button
                    loading={generating}
                    onClick={async () => {
                      if (data.agreement.isValid) {
                        await generateAgreementPdf({
                          variables: {
                            id: agreementId,
                            companyId,
                            signatureMethod: GqlDocumentSignatureMethod.Digital,
                          },
                        });
                      } else {
                        setValidatorDialog([
                          {
                            hasError: true,
                            completed: false,
                            severity: GqlSeverity.Error,
                            error: {
                              en: {
                                short: 'Missing required fields',
                                long: 'The document is missing required fields. Be sure to fill in all inputs below.',
                              },
                              sv: {
                                short: 'Obligatoriska fält saknas',
                                long: 'Dokumentet saknar obligatoriska fält. Fyll i alla fält nedan.',
                              },
                            },
                          },
                        ]);
                      }
                    }}
                    disabled={!areAllVariablesSet}
                    size="sm"
                  >
                    {data.agreement.status === GqlAgreementFlowStatus.Pending ? (
                      <Trans id="Generate PDF" />
                    ) : (
                      <Trans id="Regenerate PDF" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            )
          }
        />
      </Card>
      <ErrorBoundary fallback={<Typography>Could not render document</Typography>}>
        <DocumentPreview
          cancelSignature={() => stopProcess({ variables: { companyId, input: { id: data.agreement.file.id } } })}
          documentStatus={data.agreement.status}
          template={data.agreement.delta}
          fields={data.agreement.fields.map(convertGqlFieldValue)}
          setValue={async ({ fieldId, value }: { fieldId: string; value: unknown }) => {
            try {
              if (value instanceof Date) {
                await upsertValue({
                  variables: { valueDate: value.toISOString(), agreementId, companyId, fieldId },
                });
              } else if (typeof value === 'boolean') {
                await upsertValue({
                  variables: { valueBoolean: value, agreementId, companyId, fieldId },
                });
              } else if (typeof value === 'string') {
                await upsertValue({
                  variables: { valueString: value, agreementId, companyId, fieldId },
                });
              } else if (typeof value === 'object') {
                await upsertValue({
                  variables: { valueIdentity: value as GqlCreateIdentityInput, agreementId, companyId, fieldId },
                });
              } else {
                throw new Error('Invalid value type');
              }
            } catch (error) {
              Sentry.captureException(error);
              toast({
                title: <Trans id="Error" />,
                description: <Trans id="Could not update field" />,
                severity: 'danger',
              });
            }
          }}
        />
      </ErrorBoundary>
    </Stack>
  );
}
